import * as React from "react";
import { useParams, Link } from "react-router-dom";
import {
  Grid3x3, Plus, X, Settings2, ClipboardList, CalendarClock, ShieldAlert,
  PoundSterling, MessageSquare, Clock, ShieldCheck, Columns3, RefreshCw, Move,
} from "lucide-react";
import {
  DndContext, PointerSensor, KeyboardSensor,
  useSensor, useSensors, closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext, useSortable, arrayMove, rectSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { fmtCents, fmtTime } from "@/lib/time";
import { Skeleton } from "@/components/feel/Skeleton";
import { EmptyState } from "@/components/feel/EmptyState";

/**
 * The Wall — configurable grid of live panes per user.
 * Layout is persisted server-side (PUT /wall/layout). Reorder + resize +
 * add/remove all funnel into the same PUT.
 */
const PANE_META = {
  rota_today:            { icon: ClipboardList,  label: "Rota — today",         accent: "violet" },
  bookings_today:        { icon: CalendarClock,  label: "Bookings — today",     accent: "emerald" },
  alerts_feed:           { icon: ShieldAlert,    label: "Alerts",               accent: "coral" },
  margin_week:           { icon: PoundSterling,  label: "Margin — this week",   accent: "teal" },
  comms_unread:          { icon: MessageSquare,  label: "Comms unread",         accent: "violet" },
  timesheet_exceptions:  { icon: Clock,          label: "Timesheet exceptions", accent: "amber" },
  expiring_records:      { icon: ShieldCheck,    label: "Expiring records",     accent: "amber" },
  boards_due:            { icon: Columns3,       label: "Boards — due",         accent: "violet" },
};
const ALL_TYPES = Object.keys(PANE_META);

export default function Wall() {
  const { companyId } = useParams();
  const { has } = useCompany();
  const [layout, setLayout] = React.useState(null);
  const [savingLayout, setSavingLayout] = React.useState(false);
  const [addOpen, setAddOpen] = React.useState(false);

  const canView = has("wall.view");

  const load = React.useCallback(async () => {
    try {
      const { data } = await api.get(`/companies/${companyId}/wall/layout`);
      setLayout(data);
    } catch (e) {
      setLayout({ panes: [] });
    }
  }, [companyId]);

  React.useEffect(() => { if (canView) load(); }, [load, canView]);

  const persist = React.useCallback(async (nextPanes) => {
    setSavingLayout(true);
    try {
      const payload = { panes: nextPanes.map((p) => ({
        id: p.id, type: p.type, size: p.size || "1x1", config: p.config || {},
      })) };
      const { data } = await api.put(`/companies/${companyId}/wall/layout`, payload);
      setLayout(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Layout save failed");
    } finally { setSavingLayout(false); }
  }, [companyId]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function onDragEnd(evt) {
    const { active, over } = evt;
    if (!over || active.id === over.id) return;
    const panes = layout?.panes || [];
    const oldIdx = panes.findIndex((p) => p.id === active.id);
    const newIdx = panes.findIndex((p) => p.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const next = arrayMove(panes, oldIdx, newIdx);
    setLayout({ ...layout, panes: next });
    persist(next);
  }

  function resize(paneId, size) {
    const next = (layout?.panes || []).map((p) =>
      p.id === paneId ? { ...p, size } : p,
    );
    setLayout({ ...layout, panes: next });
    persist(next);
  }

  function remove(paneId) {
    const next = (layout?.panes || []).filter((p) => p.id !== paneId);
    setLayout({ ...layout, panes: next });
    persist(next);
  }

  function add(type) {
    const id = `p_${Math.random().toString(36).slice(2, 10)}`;
    const next = [...(layout?.panes || []), { id, type, size: "1x1", config: {} }];
    setLayout({ ...layout, panes: next });
    setAddOpen(false);
    persist(next);
  }

  if (!canView) {
    return (
      <div className="p-6" data-testid="wall-forbidden">
        <EmptyState icon={Grid3x3} eyebrow="Wall"
          title="You don't have wall.view."
          body="Ask an admin to grant wall.view on your role." />
      </div>
    );
  }

  if (!layout) {
    return (
      <div className="px-4 md:px-6 py-4 md:py-6" data-testid="wall-loading">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {[0,1,2,3,4,5].map((i) => <Skeleton key={i} className="h-56 w-full" />)}
        </div>
      </div>
    );
  }

  const panes = layout.panes || [];

  return (
    <div className="px-4 md:px-6 py-4 md:py-6 max-w-[1600px]" data-testid="wall-page">
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div>
          <div className="micro-label">Live</div>
          <h1 className="mt-1 text-[22px] font-bold tracking-tight text-brand-ink flex items-center gap-2">
            <Grid3x3 size={18} className="text-brand-violet" /> The Wall
          </h1>
          <div className="mt-1 text-[12px] text-brand-muted">
            Your at-a-glance dashboard. Drag tiles to reorder. Auto-refreshes on the pulse.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button className="btn-outline !h-8" onClick={load}
                  data-testid="wall-refresh">
            <RefreshCw size={13} className={savingLayout ? "animate-spin" : ""} /> Refresh
          </button>
          <button className="btn-violet !h-8" onClick={() => setAddOpen(true)}
                  data-testid="wall-add-pane">
            <Plus size={13}/> Add pane
          </button>
        </div>
      </div>

      {panes.length === 0 ? (
        <EmptyState icon={Grid3x3} eyebrow="Wall"
          title="Nothing on the Wall yet."
          body="Add panes to see live rotas, bookings, alerts, margin, comms and records."
          action={<button className="btn-violet" onClick={() => setAddOpen(true)}
                          data-testid="wall-empty-add">
                    <Plus size={13}/> Add your first pane
                  </button>}
          testid="wall-empty" />
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={panes.map((p) => p.id)} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 items-start" data-testid="wall-grid">
              {panes.map((p) => (
                <SortablePane
                  key={p.id} pane={p} companyId={companyId}
                  onResize={(s) => resize(p.id, s)}
                  onRemove={() => remove(p.id)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {addOpen && (
        <AddPaneDialog onClose={() => setAddOpen(false)} onAdd={add} existing={panes} />
      )}
    </div>
  );
}

function SortablePane({ pane, companyId, onResize, onRemove }) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: pane.id });
  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const wide = pane.size === "2x1";
  return (
    <div ref={setNodeRef}
         style={style}
         className={`card-panel ${wide ? "sm:col-span-2 lg:col-span-2" : ""}`}
         data-testid={`wall-pane-${pane.type}`}>
      <div className="flex items-center justify-between px-3 py-2 border-b border-brand-rule">
        <PaneHeader pane={pane} />
        <div className="flex items-center gap-1">
          <button className="btn-ghost !h-7 !w-7 !p-0" title="Toggle size"
                  onClick={() => onResize(wide ? "1x1" : "2x1")}
                  data-testid={`wall-pane-${pane.id}-resize`}>
            <Settings2 size={12}/>
          </button>
          <button className="btn-ghost !h-7 !w-7 !p-0 cursor-grab active:cursor-grabbing"
                  title="Drag to reorder"
                  {...attributes} {...listeners}
                  data-testid={`wall-pane-${pane.id}-drag`}>
            <Move size={12}/>
          </button>
          <button className="btn-ghost !h-7 !w-7 !p-0 text-brand-coral" title="Remove"
                  onClick={onRemove}
                  data-testid={`wall-pane-${pane.id}-remove`}>
            <X size={12}/>
          </button>
        </div>
      </div>
      <div className="p-3">
        <PaneBody pane={pane} companyId={companyId} />
      </div>
    </div>
  );
}

function PaneHeader({ pane }) {
  const meta = PANE_META[pane.type] || { icon: Grid3x3, label: pane.type, accent: "violet" };
  const Icon = meta.icon;
  return (
    <div className="flex items-center gap-2 min-w-0">
      <span className={`h-6 w-6 rounded-md flex items-center justify-center text-brand-violet bg-brand-hush`}>
        <Icon size={13} />
      </span>
      <div className="min-w-0">
        <div className="text-[12.5px] font-semibold text-brand-ink truncate">{meta.label}</div>
      </div>
    </div>
  );
}

function PaneBody({ pane, companyId }) {
  const [state, setState] = React.useState({ loading: true, data: null, error: null });
  const REFRESH_MS = 30_000;

  const fetch = React.useCallback(async () => {
    try {
      const params = {};
      if (pane.config?.site_id) params.site_id = pane.config.site_id;
      if (pane.config?.status)  params.status  = pane.config.status;
      const { data } = await api.get(`/companies/${companyId}/wall/pane/${pane.type}`, { params });
      setState({ loading: false, data, error: null });
    } catch (e) {
      setState({ loading: false, data: null,
                 error: formatApiErrorDetail(e?.response?.data?.detail) || "Load failed" });
    }
  }, [pane.type, pane.config?.site_id, pane.config?.status, companyId]);

  React.useEffect(() => {
    fetch();
    const id = setInterval(fetch, REFRESH_MS);
    return () => clearInterval(id);
  }, [fetch]);

  // React to SSE bus refetches — cheap
  React.useEffect(() => {
    const on = (e) => {
      const t = e.detail?.type;
      if (!t) return;
      const map = {
        rota_today: /shift\.|rota\./,
        bookings_today: /booking\./,
        alerts_feed: /rule_event\./,
        comms_unread: /message\.|conversation\./,
        timesheet_exceptions: /time_entry\.|shift\./,
        expiring_records: /record\./,
        boards_due: /card\.|board\./,
      };
      const rx = map[pane.type];
      if (rx && rx.test(t)) fetch();
    };
    window.addEventListener("fable:event", on);
    return () => window.removeEventListener("fable:event", on);
  }, [pane.type, fetch]);

  if (state.loading) return <Skeleton className="h-32 w-full" />;
  if (state.error) return <div className="text-[11.5px] text-brand-coral">{state.error}</div>;

  switch (pane.type) {
    case "rota_today":            return <PaneRotaToday data={state.data} companyId={companyId} />;
    case "bookings_today":        return <PaneBookingsToday data={state.data} companyId={companyId} />;
    case "alerts_feed":           return <PaneAlerts data={state.data} companyId={companyId} />;
    case "margin_week":           return <PaneMargin data={state.data} />;
    case "comms_unread":          return <PaneCommsUnread data={state.data} companyId={companyId} />;
    case "timesheet_exceptions":  return <PaneTimesheetExceptions data={state.data} />;
    case "expiring_records":      return <PaneExpiringRecords data={state.data} companyId={companyId} />;
    case "boards_due":            return <PaneBoardsDue data={state.data} companyId={companyId} />;
    default: return <div className="text-[11.5px] text-brand-muted">Unknown pane</div>;
  }
}

// ---------------- Pane bodies ----------------

function PaneRotaToday({ data, companyId }) {
  const shifts = data?.shifts || [];
  if (!shifts.length) return <PaneEmpty text="Nothing on today's rota." />;
  return (
    <div className="space-y-1.5 max-h-[280px] overflow-y-auto">
      {shifts.slice(0, 12).map((s) => (
        <Link key={s.id} to={`/c/${companyId}/m/rotas`}
              className="flex items-center gap-2 rounded-md border border-brand-rule px-2 py-1.5 hover:bg-brand-hush"
              data-testid={`wall-rota-${s.id}`}>
          <span className="mono-num text-[10.5px] text-brand-muted w-[70px]">
            {fmtTime(s.start)}–{fmtTime(s.end)}
          </span>
          <span className="text-[12px] text-brand-ink truncate flex-1">
            {s.position || "shift"}
          </span>
          <span className={`chip mono-num text-[9.5px] ${s.member_id ? '' : 'chip-amber'}`}>
            {s.member_id ? "assigned" : "open"}
          </span>
        </Link>
      ))}
    </div>
  );
}

function PaneBookingsToday({ data, companyId }) {
  const rows = data?.bookings || [];
  if (!rows.length) return <PaneEmpty text="No bookings today." />;
  return (
    <div className="space-y-1.5 max-h-[280px] overflow-y-auto">
      {rows.slice(0, 12).map((b) => (
        <Link key={b.id} to={`/c/${companyId}/m/bookings`}
              className="flex items-center gap-2 rounded-md border border-brand-rule px-2 py-1.5 hover:bg-brand-hush"
              data-testid={`wall-booking-${b.id}`}>
          <span className="mono-num text-[10.5px] text-brand-muted w-[54px]">
            {fmtTime(b.starts_at)}
          </span>
          <span className="text-[12px] text-brand-ink truncate flex-1">
            {b.customer?.name || "—"}
            <span className="text-brand-muted"> · {b.service_name}</span>
          </span>
          <StatusChip s={b.status} />
        </Link>
      ))}
    </div>
  );
}

function PaneAlerts({ data, companyId }) {
  const rows = data?.alerts || [];
  if (!rows.length) return <PaneEmpty text="All clear." />;
  return (
    <div className="space-y-1.5 max-h-[280px] overflow-y-auto">
      {rows.slice(0, 10).map((a) => (
        <Link key={a.id} to={`/c/${companyId}/alerts`}
              className="flex items-center gap-2 rounded-md border border-brand-rule px-2 py-1.5 hover:bg-brand-hush"
              data-testid={`wall-alert-${a.id}`}>
          <span className={`chip mono-num text-[9.5px] ${
            a.severity === "alert" ? "chip-coral"
            : a.severity === "warn" ? "chip-amber" : ""}`}>
            {a.severity}
          </span>
          <span className="text-[12px] text-brand-ink truncate flex-1">{a.title}</span>
        </Link>
      ))}
    </div>
  );
}

function PaneMargin({ data }) {
  const m = data?.margin;
  if (!m) return <PaneEmpty text="No books data for this week." />;
  return (
    <div className="space-y-2">
      <div className="mono-num text-[26px] font-bold text-brand-ink"
           data-testid="wall-margin-net">{fmtCents(m.net_margin_cents)}</div>
      <div className="text-[10.5px] text-brand-muted mono-num">week of {m.week_start}</div>
      <div className="grid grid-cols-2 gap-1.5 text-[11px]">
        <MarginLine label="Takings" v={m.takings_cents} tone="pos" />
        <MarginLine label="Booking rev" v={m.bookings_revenue_cents} tone="pos" />
        <MarginLine label="Card spend" v={-m.card_spend_cents} tone="neg" />
        <MarginLine label="Labour" v={-m.labour_cents} tone="neg" />
      </div>
    </div>
  );
}
function MarginLine({ label, v, tone }) {
  return (
    <div className="flex justify-between border-t border-brand-rule pt-1">
      <span className="text-brand-muted">{label}</span>
      <span className={`mono-num ${tone === 'neg' ? 'text-brand-coral' : 'text-emerald-700'}`}>
        {fmtCents(v)}
      </span>
    </div>
  );
}

function PaneCommsUnread({ data, companyId }) {
  const d = data?.unread || {};
  const rows = (d.per_conversation || []).filter((r) => r.unread > 0);
  if (!rows.length) return <PaneEmpty text="Inbox clear." />;
  return (
    <div>
      <div className="mb-2 mono-num text-[22px] font-bold text-brand-ink" data-testid="wall-comms-total">
        {d.total_unread || 0}
      </div>
      <div className="space-y-1 max-h-[220px] overflow-y-auto">
        {rows.slice(0, 8).map((r) => (
          <Link key={r.conversation_id} to={`/c/${companyId}/m/comms`}
                className="flex items-center gap-2 rounded-md border border-brand-rule px-2 py-1.5 hover:bg-brand-hush">
            <span className="text-[12px] text-brand-ink truncate flex-1">
              {r.kind === "channel" ? `#${r.slug}` : (r.last_sender_name || "DM")}
            </span>
            <span className="chip mono-num text-[9.5px] chip-violet">{r.unread}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}

function PaneTimesheetExceptions({ data }) {
  const rows = data?.entries || [];
  if (!rows.length) return <PaneEmpty text="No exceptions." />;
  return (
    <div className="space-y-1.5 max-h-[280px] overflow-y-auto">
      {rows.slice(0, 12).map((e) => (
        <div key={e.id} className="flex items-center gap-2 rounded-md border border-brand-rule px-2 py-1.5"
             data-testid={`wall-timesheet-${e.id}`}>
          <span className="mono-num text-[10.5px] text-brand-muted w-[70px]">
            {(e.clock_in_at || "").slice(11, 16)}
          </span>
          <span className="text-[12px] text-brand-ink truncate flex-1">{e.user_id?.slice(0, 8)}</span>
          <div className="flex flex-wrap gap-1">
            {(e.exceptions || []).map((x) => (
              <span key={x} className={`chip mono-num text-[9.5px] ${x === 'record_lapsed' ? 'chip-coral' : 'chip-amber'}`}>
                {x}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function PaneExpiringRecords({ data, companyId }) {
  const rows = data?.records || [];
  if (!rows.length) return <PaneEmpty text="Nothing expiring." />;
  return (
    <div className="space-y-1.5 max-h-[280px] overflow-y-auto">
      {rows.slice(0, 12).map((r) => (
        <Link key={r.id} to={`/c/${companyId}/m/hr?tab=records`}
              className="flex items-center gap-2 rounded-md border border-brand-rule px-2 py-1.5 hover:bg-brand-hush"
              data-testid={`wall-record-${r.id}`}>
          <span className={`chip mono-num text-[9.5px] ${r.status === 'lapsed' ? 'chip-coral' : 'chip-amber'}`}>
            {r.status === "lapsed" ? "lapsed" : "expiring"}
          </span>
          <span className="text-[12px] text-brand-ink truncate flex-1">
            {r.type_label}
            <span className="text-brand-muted"> · {r.subject_name || r.subject_id?.slice(0, 6)}</span>
          </span>
          <span className="mono-num text-[10.5px] text-brand-muted">{r.expires_at?.slice(0, 10)}</span>
        </Link>
      ))}
    </div>
  );
}

function PaneBoardsDue({ data, companyId }) {
  const rows = data?.cards || [];
  if (!rows.length) return <PaneEmpty text="Nothing due." />;
  return (
    <div className="space-y-1.5 max-h-[280px] overflow-y-auto">
      {rows.slice(0, 12).map((c) => (
        <Link key={c.id} to={`/c/${companyId}/m/boards/${c.board_id}`}
              className="flex items-center gap-2 rounded-md border border-brand-rule px-2 py-1.5 hover:bg-brand-hush"
              data-testid={`wall-card-${c.id}`}>
          <span className="text-[12px] text-brand-ink truncate flex-1">{c.title || c.name || "card"}</span>
          <span className={`mono-num text-[10.5px] ${c.overdue ? 'text-brand-coral' : 'text-brand-muted'}`}>
            {c.due_date?.slice(0, 10)}
          </span>
        </Link>
      ))}
    </div>
  );
}

function PaneEmpty({ text }) {
  return <div className="text-[11.5px] text-brand-faint py-4 text-center">{text}</div>;
}

function StatusChip({ s }) {
  const map = {
    pending: "chip-amber", confirmed: "chip-violet", completed: "chip-emerald",
    cancelled: "chip-coral", no_show: "chip-coral",
  };
  return <span className={`chip mono-num text-[9.5px] ${map[s] || ''}`}>{s}</span>;
}

// ---------------- Add Pane Dialog ----------------

function AddPaneDialog({ onClose, onAdd, existing }) {
  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex items-end sm:items-center justify-center p-4"
         onClick={onClose} data-testid="wall-add-dialog">
      <div className="bg-card rounded-lg shadow-card w-full max-w-lg overflow-hidden"
           onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-brand-rule flex items-center justify-between">
          <div>
            <div className="micro-label">Wall</div>
            <div className="text-[14px] font-semibold text-brand-ink">Add pane</div>
          </div>
          <button className="btn-ghost !h-7 !w-7 !p-0" onClick={onClose}>
            <X size={14}/>
          </button>
        </div>
        <div className="p-3 grid grid-cols-2 gap-2">
          {ALL_TYPES.map((t) => {
            const meta = PANE_META[t];
            const Icon = meta.icon;
            return (
              <button key={t} onClick={() => onAdd(t)}
                      className="flex items-start gap-2 rounded-md border border-brand-rule px-3 py-3 text-left hover:bg-brand-hush"
                      data-testid={`wall-add-${t}`}>
                <span className="h-7 w-7 rounded-md bg-brand-hush text-brand-violet flex items-center justify-center">
                  <Icon size={14} />
                </span>
                <div className="min-w-0">
                  <div className="text-[13px] font-semibold text-brand-ink">{meta.label}</div>
                  <div className="text-[10.5px] text-brand-muted mono-num">{t}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
